import { useMemo } from "react";
import { useLocalization } from "@fluent/react";
import { Tabs } from "@narmi/design_system";
import type { NetWorthPeriod } from "./TrendChart/hooks";
import styles from "./NetWorthPeriodTabs.module.scss";

const NET_WORTH_PERIODS: readonly NetWorthPeriod[] = [
  "semiannual",
  "nine_month",
  "annual",
];

type NetWorthPeriodTabsProps = {
  period: NetWorthPeriod;
  availablePeriods: NetWorthPeriod[];
  /** False while loading or erroring - nothing hidden until true. */
  canApplyHiding: boolean;
  onPeriodChange: (period: NetWorthPeriod) => void;
};

const periodTabLabel = (
  l10n: ReturnType<typeof useLocalization>["l10n"],
  period: NetWorthPeriod
) => {
  switch (period) {
    case "nine_month":
      return l10n.getString("dashboard-net-worth-chart-tab-9-months");
    case "annual":
      return l10n.getString("dashboard-net-worth-chart-tab-1-year");
    default:
      return l10n.getString("dashboard-net-worth-chart-tab-6-months");
  }
};

const NetWorthPeriodTabs = ({
  period,
  availablePeriods,
  canApplyHiding,
  onPeriodChange,
}: NetWorthPeriodTabsProps) => {
  const { l10n } = useLocalization();

  // "semiannual" is always shown, regardless of availability.
  const visiblePeriods = useMemo(
    () =>
      NET_WORTH_PERIODS.filter(
        (p) => p === "semiannual" || !canApplyHiding || availablePeriods.includes(p)
      ),
    [availablePeriods, canApplyHiding]
  );

  const handleTabChange = (index: number) => {
    const newPeriod = visiblePeriods[index];
    if (newPeriod) onPeriodChange(newPeriod);
  };

  return (
    <div className={styles.periodTabs}>
      <Tabs
        onTabChange={handleTabChange}
        selectedIndex={Math.max(0, visiblePeriods.indexOf(period))}
      >
        <Tabs.List>
          {visiblePeriods.map((p) => (
            <Tabs.Tab key={p} label={periodTabLabel(l10n, p)} tabId={p} />
          ))}
        </Tabs.List>
      </Tabs>
    </div>
  );
};

export default NetWorthPeriodTabs;
